import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {BymoPageProps, DoppeActionPriceData} from '../../client-server-common';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    BymoPagePropsSpecialContentSettingMetadata,
    bymoPagePropsCreateContentSettingMetadata,
    DoppeBusinessEntity,
    doppeBusinessEntityDtoIsNotFound,
    DoppeExternalEventProvider,
    doppeExternalEventProviderResolveBusinessEntityReference,
    DoppeLinkSpecEntityType,
    doppeLinkSpecCreateEntity
} from '../../client-server-common';
import {DoppeSupportedWixTpas} from '../../client-server-common/types/doppe-supported-wix-tpas';
import {DoppeDtoUserMetaSiteType} from '../../client-server-common/types/doppe-dto-user';
import {bymoPageContextPropsResolveBusinessEntity} from '../../doppe-sdk/bymo-page/bymo-page-context-props';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {DoppeActionTypeEventViewerLang} from './doppe-action-type-event-viewer.lang';
import type {HideableValue, OverridableValue, RichTextContent, SerializedDateTimeValueAsString} from '@wix/devzai-utils-common';
import {
    arrayRemoveNullishValues,
    arrayRemoveNullValues,
    asArray,
    dateResolveFromDateTimeValue,
    evaluateFunction,
    dateSerializedToString,
    hideableValueCreateHidden,
    hideableValueGetValue,
    hideableValueIsVisibleAndNotEqualValue,
    hideableValueModifyValue,
    immerProduce,
    overridableValueCreateOriginal,
    overridableValueGetOverriddenValue,
    overridableValueGetValue,
    overridableValueIsOriginal,
    stringIsNotNullableOrWhiteSpace
} from '@wix/devzai-utils-common';
import type {DoppeDtoEvent} from '../../client-server-common/types/doppe-dto-event';
import {
    doppeViewerIntlFormatEventTimeFromStartDate
} from '../../client-server-common/doppe-viewer/doppe-viewer-intl-utils/doppe-viewer-intl-utils';
import type {DoppeAppPageContentLocales} from '../../client/doppe-app-locales/doppe-app-page-content-locales';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonsResetSettingsForLock,
    doppeActionCtaButtonTypeGetDefaultSettings,
    DoppeActionCTAType,
    doppeActionMultipleCTAButtonCreate
} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';

export namespace DoppeActionTypeEvent {

    export interface EventDateData {
        startDate: SerializedDateTimeValueAsString;
        startTime: string;
        endDate: SerializedDateTimeValueAsString;
        endTime: string;
        /** TZ database format (e.g., "America/New_York"). Null when timezone should not be displayed. */
        timeZoneId: string | null;
        isDateTBD: boolean;
        /** When visible, overrides the formatted date display with this custom text. */
        customText: HideableValue<string>;
    }

    export interface EventSettings  {
        eventLink: string;
        eventTitle: HideableValue<OverridableValue<string>>;
        description: HideableValue<OverridableValue<RichTextContent>>;
        eventImages: OverridableValue<WixMediaResource[] | null>;
        eventDateData: HideableValue<OverridableValue<EventDateData>>;
        eventLocation: HideableValue<OverridableValue<string>>;
        priceData: HideableValue<OverridableValue<DoppeActionPriceData>>;
        couponCode: HideableValue<string>;
        ribbon: HideableValue<string>;
        eventProvider: DoppeExternalEventProvider | null;
        eventProviderId: string | null;
    }

    export interface ActionSettings extends DoppeActionTypeEvent.EventSettings,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
    }

    export type DeprecatedFields = Partial<DoppeActionCtaButtonSettings> & {
        eventButtonText?: HideableValue<string>;
        eventDate?: HideableValue<OverridableValue<SerializedDateTimeValueAsString>>;
        eventTime?: HideableValue<OverridableValue<string>>;
        eventEndDate?: HideableValue<OverridableValue<SerializedDateTimeValueAsString>>;
        eventEndTime?: HideableValue<OverridableValue<string>>;
        timeZoneId?: HideableValue<OverridableValue<string | null>>;
        isDateTBD?: OverridableValue<boolean>;
    }

    export type PartialProps = Partial<ActionSettings> & DeprecatedFields;
}

export const doppeActionTypeEvent = doppeActionTypeDefineMetadata<DoppeActionTypeEvent.ActionSettings, DoppeActionTypeEvent.PartialProps>({
    id: 'io.bymo.action/event',
    resolveActionSettings: action => {

        const {
            eventLink = '',
            eventButtonText = DoppeActionTypeEventViewerLang.defaultTexts.eventButtonText,
            ctaButton,
            ctaButtons,
            sections,
            priceData,
            eventDateData = doppeActionTypeEventCreateDateDataFromLegacyFields({}),
            ribbon = hideableValueCreateHidden('SALE'),
            ...restSettings
        } = action.settings;

        return {
            eventLink: eventLink,
            eventTitle: DoppeActionTypeEventViewerLang.defaultTexts.eventTitle,
            description: DoppeActionTypeEventViewerLang.defaultTexts.description,
            eventButtonText: eventButtonText,
            eventImages: null,
            eventDateData: eventDateData,
            eventLocation: '',
            priceData: hideableValueModifyValue(priceData ?? {actualPrice: '', discountPrice: ''}, overridableValue => {
                const innerValue = overridableValueGetValue(overridableValue);
                const resolved = {...innerValue, discountPrice: innerValue.discountPrice ?? ''};
                return overridableValueIsOriginal(overridableValue)
                    ? overridableValueCreateOriginal(resolved)
                    : resolved;
            }),
            couponCode: hideableValueCreateHidden('CouponCode'),
            ribbon: ribbon,
            eventProvider: null,
            eventProviderId: null,
            ...getDefaultDoppeActionCtaButtonSettings({
                buttonText: eventButtonText,
                showButton: true,
            }),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                hasSourceURL: true,
                ctaButton: ctaButton,
            }),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, true),
            ...restSettings
        }
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            eventLink: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl),
            eventTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            eventImages: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            eventDateData: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
            eventLocation: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
            couponCode: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityCouponCode),
            priceData: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.PriceData, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
            ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon),
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {
                [DoppeActionCTAType.Register]: true
            }
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                couponCode: true,
                description: true,
                eventDateData: true,
                eventLocation: true,
                eventTitle: true,
                priceData: true,
                ribbon: true,
            },
        bindableContentSettingsNames: {
                description: true,
                eventDateData: true,
                eventImages: true,
                eventLocation: true,
                eventTitle: true,
                priceData: true
            }
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    isLinkOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            actionSettings.eventLink = '';
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveUsedMedia: actionSettings => {
        const eventImages = overridableValueGetValue(actionSettings.eventImages);
        return arrayRemoveNullValues(asArray(eventImages));
    },
    resolveMainMedia: (actionSettings, bymoPageContextProps) => {
        const doppeDtoEvent = actionSettings.eventProvider && actionSettings.eventProviderId ? (() => {
            const entityDto = bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                doppeExternalEventProviderResolveBusinessEntityReference(actionSettings.eventProvider!, actionSettings.eventProviderId!)
            );
            return entityDto && !doppeBusinessEntityDtoIsNotFound(entityDto) ? entityDto as DoppeDtoEvent : null;
        })() : null;

        const eventImages = overridableValueGetOverriddenValue(
            actionSettings.eventImages,
            doppeDtoEvent?.media ?? null
        );
        return eventImages ? asArray(eventImages)[0] ?? null : null;
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        const sourceLinkSpec = evaluateFunction(() => {
            if (actionSettings.eventProvider === DoppeExternalEventProvider.Hopp && actionSettings.eventProviderId !== null) {
                return doppeLinkSpecCreateEntity(DoppeLinkSpecEntityType.Event, actionSettings.eventProviderId);
            }
            return stringIsNotNullableOrWhiteSpace(actionSettings.eventLink) ? actionSettings.eventLink : null;
        });

        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: sourceLinkSpec,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    resolveCouponData: (actionSettings, bymoPageContextProps) => {
        const doppeDtoEvent = actionSettings.eventProvider && actionSettings.eventProviderId ? (() => {
            const entityDto = bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                doppeExternalEventProviderResolveBusinessEntityReference(actionSettings.eventProvider!, actionSettings.eventProviderId!)
            );
            return entityDto && !doppeBusinessEntityDtoIsNotFound(entityDto) ? entityDto as DoppeDtoEvent : null;
        })() : null;

        const description = hideableValueModifyValue(
            actionSettings.description,
            value => overridableValueGetOverriddenValue(value, doppeDtoEvent?.description ?? '')
        );
        const eventTitle = hideableValueModifyValue(
            actionSettings.eventTitle,
            value => overridableValueGetOverriddenValue(value, doppeDtoEvent?.name ?? '')
        );
        return {
            showCouponCode: hideableValueIsVisibleAndNotEqualValue(actionSettings.couponCode, ''),
            couponCode: hideableValueGetValue(actionSettings.couponCode),
            couponDescription: hideableValueIsVisibleAndNotEqualValue(description, '') ? description : '',
            couponTitle: hideableValueIsVisibleAndNotEqualValue(eventTitle, '') ? eventTitle : '',
        }
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            const eventImages = overridableValueGetValue(actionSettings.eventImages);
            if (eventImages) {
                const images = asArray(eventImages);
                actionSettings.eventImages = images.map(image => {
                    return wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap)
                })
            }
        })
    },
    resolveUsedBusinessEntities: actionSettings => {
        return arrayRemoveNullValues([
            actionSettings.eventProvider !== null && actionSettings.eventProviderId !== null ?
                doppeExternalEventProviderResolveBusinessEntityReference(
                    actionSettings.eventProvider,
                    actionSettings.eventProviderId
                ) :
                null
        ])
    },
    resolveActionEntityBinding: actionSettings => {
        const {eventProvider, eventProviderId} = actionSettings;
        return eventProvider !== null && eventProviderId !== null
            ? doppeExternalEventProviderResolveBusinessEntityReference(eventProvider, eventProviderId)
            : null;
    },
    getSupportedBoundEntities: ({linkedMetaSiteInstalledTpas, creatorsMetaSiteInstalledTpas}) =>
        arrayRemoveNullishValues([
            linkedMetaSiteInstalledTpas.includes(DoppeSupportedWixTpas.Events) ? DoppeBusinessEntity.WixEvent : undefined,
            creatorsMetaSiteInstalledTpas.includes(DoppeSupportedWixTpas.Events) ? DoppeBusinessEntity.HoppEvent : undefined,
        ]),
    getSupportedBoundEntitiesByTpa: () => ({
        [DoppeDtoUserMetaSiteType.LinkedMetaSite]: {
            [DoppeSupportedWixTpas.Events]: [DoppeBusinessEntity.WixEvent],
        },
        [DoppeDtoUserMetaSiteType.CreatorsMetaSite]: {
            [DoppeSupportedWixTpas.Events]: [DoppeBusinessEntity.HoppEvent],
        },
    }),
    resolveActualActionSettings: (actionSettings, bymoPageContextProps, bymoPageProps) => {
        const {eventProvider, eventProviderId} = actionSettings;
        if (eventProvider !== null && eventProviderId !== null) {
            const boundEvent = bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                doppeExternalEventProviderResolveBusinessEntityReference(eventProvider, eventProviderId)
            );
            if (boundEvent && !doppeBusinessEntityDtoIsNotFound(boundEvent)) {
                return {
                    ...actionSettings,
                    ...doppeActionTypeEventResolveSettingsFromBoundEntity(actionSettings, boundEvent as DoppeDtoEvent, bymoPageProps),
                };
            }
        }
        return actionSettings;
    },
});

export function doppeActionTypeEventResolveSettingsFromBoundEntity(
    actionSettings: DoppeActionTypeEvent.EventSettings,
    doppeDtoEvent: DoppeDtoEvent,
    _bymoPageProps: BymoPageProps,
): DoppeActionTypeEvent.EventSettings {

    const entityDateData = doppeActionTypeEventCreateDateDataFromDtoEvent(doppeDtoEvent);
    const entityPriceData = calculatePriceDataForEvent(doppeDtoEvent);

    return {
        ...actionSettings,
        eventLink: stringIsNotNullableOrWhiteSpace(doppeDtoEvent.eventPageUrl) ?
            doppeDtoEvent.eventPageUrl : 'about:blank',
        eventImages: overridableValueGetOverriddenValue(actionSettings.eventImages, doppeDtoEvent.media),
        eventTitle: hideableValueModifyValue(
            actionSettings.eventTitle,
            value => overridableValueGetOverriddenValue(value, doppeDtoEvent.name ?? '')
        ),
        description: hideableValueModifyValue(
            actionSettings.description,
            value => overridableValueGetOverriddenValue(value, doppeDtoEvent.description ?? '')
        ),
        priceData: hideableValueModifyValue(
            actionSettings.priceData,
            value => overridableValueGetOverriddenValue(value, entityPriceData)
        ),
        eventLocation: hideableValueModifyValue(
            actionSettings.eventLocation,
            value => overridableValueGetOverriddenValue(value, doppeDtoEvent.location ?? '')
        ),
        eventDateData: hideableValueModifyValue(
            actionSettings.eventDateData,
            value => overridableValueGetOverriddenValue(value, entityDateData)
        ),
    }
}

export function convertDoppeWixEventToEventSettings(event: DoppeDtoEvent, settings?: DoppeActionTypeEvent.ActionSettings, eventProvider?: DoppeExternalEventProvider) {

    const entityDateData = doppeActionTypeEventCreateDateDataFromDtoEvent(event);

    return {
        couponCode: hideableValueCreateHidden(''),
        ribbon: hideableValueCreateHidden(''),
        sections: [],
        ctaButtons: [doppeActionMultipleCTAButtonCreate({
            text: 'Book now',
            isRequired: false,
            settings: doppeActionCtaButtonTypeGetDefaultSettings(DoppeActionCTAType.Link, true)
        })],
        ...settings,
        eventImages: overridableValueCreateOriginal(event.media),
        eventLink: event.eventPageUrl ?? '',
        description: event.description
            ? overridableValueCreateOriginal(event.description)
            : hideableValueCreateHidden(overridableValueCreateOriginal('')),
        eventTitle: overridableValueCreateOriginal(event?.name ? event.name : ''),
        eventLocation: overridableValueCreateOriginal(event?.location ?? ''),
        eventDateData: overridableValueCreateOriginal(entityDateData),
        priceData: calculateActualPriceForEvent(event),
        eventProviderId: event.id,
        eventProvider: eventProvider ?? DoppeExternalEventProvider.Wix,
    }
}

export function populateEventActionSettingsDefaultTexts(options: {
    event: DoppeActionTypeEvent.ActionSettings,
    pageContentLocales: DoppeAppPageContentLocales,
}) : DoppeActionTypeEvent.ActionSettings {

    const {
        event,
        pageContentLocales,
    } = options;

    return  {
        ...event,
        eventTitle: overridableValueGetValue(hideableValueGetValue(event.eventTitle)) !== '' ? event.eventTitle : overridableValueCreateOriginal(pageContentLocales["event.actionSettings.title"]),
        description: hideableValueModifyValue(event.description, (overridable) => {
            const value = overridableValueGetValue(overridable);
            return value !== '' ? overridable : overridableValueCreateOriginal(pageContentLocales["event.actionSettings.description"]);
        }),
        couponCode: hideableValueModifyValue(event.couponCode, (value) => {
            return value !== '' ? value : pageContentLocales["event.actionSettings.couponCode"];
        }),
        ribbon: hideableValueModifyValue(event.ribbon, (value) => {
            return value !== '' ? value : pageContentLocales["event.actionSettings.ribbon"];
        }),
        eventLocation: hideableValueModifyValue(event.eventLocation, (overridable) => {
            const value = overridableValueGetValue(overridable);
            return value !== '' ? overridable : overridableValueCreateOriginal(pageContentLocales["event.actionSettings.location"]);
        }),
        sections: [],
        ctaButtons: [doppeActionMultipleCTAButtonCreate({
            text: pageContentLocales["event.actionSettings.buttonText"],
            isRequired: false,
            settings: doppeActionCtaButtonTypeGetDefaultSettings(DoppeActionCTAType.Link, true)
        })],
    }
}

export function doppeActionTypeEventCreateDateDataFromDtoEvent(doppeDtoEvent: DoppeDtoEvent): DoppeActionTypeEvent.EventDateData {
    const isDateTBD = doppeDtoEvent.eventDates?.eventStartDate === null;

    const formattedStartTime = doppeViewerIntlFormatEventTimeFromStartDate(
        doppeDtoEvent.eventDates?.eventStartDate ?? null,
        doppeDtoEvent.eventDates?.timeZoneId
    );
    const formattedEndTime = doppeViewerIntlFormatEventTimeFromStartDate(
        doppeDtoEvent.eventDates?.eventEndDate ?? null,
        doppeDtoEvent.eventDates?.timeZoneId
    );

    return {
        startDate: doppeDtoEvent.eventDates?.eventStartDate
            ? dateSerializedToString(dateResolveFromDateTimeValue(doppeDtoEvent.eventDates.eventStartDate))
            : dateSerializedToString(new Date()),
        startTime: formattedStartTime ?? '',
        endDate: doppeDtoEvent.eventDates?.eventEndDate
            ? dateSerializedToString(dateResolveFromDateTimeValue(doppeDtoEvent.eventDates.eventEndDate))
            : dateSerializedToString(new Date()),
        endTime: formattedEndTime ?? '',
        timeZoneId: doppeDtoEvent.eventDates?.showTimeZone
            ? (doppeDtoEvent.eventDates?.timeZoneId ?? null)
            : null,
        isDateTBD: isDateTBD,
        customText: doppeDtoEvent.eventDates?.formattedEventDates ?? hideableValueCreateHidden(''),
    };
}

export function doppeActionTypeEventCreateDateDataFromLegacyFields(fields: {
    eventDate?: HideableValue<OverridableValue<SerializedDateTimeValueAsString>>;
    eventTime?: HideableValue<OverridableValue<string>>;
    eventEndDate?: HideableValue<OverridableValue<SerializedDateTimeValueAsString>>;
    eventEndTime?: HideableValue<OverridableValue<string>>;
    timeZoneId?: HideableValue<OverridableValue<string | null>>;
    isDateTBD?: OverridableValue<boolean>;
}): HideableValue<OverridableValue<DoppeActionTypeEvent.EventDateData>> {
    const startDate = fields.eventDate
        ? overridableValueGetValue(hideableValueGetValue(fields.eventDate))
        : dateSerializedToString(new Date());
    const startTime = fields.eventTime
        ? overridableValueGetValue(hideableValueGetValue(fields.eventTime))
        : '';
    const endDate = fields.eventEndDate
        ? overridableValueGetValue(hideableValueGetValue(fields.eventEndDate))
        : startDate;
    const endTime = fields.eventEndTime
        ? overridableValueGetValue(hideableValueGetValue(fields.eventEndTime))
        : '';
    const timeZoneId = fields.timeZoneId
        ? overridableValueGetValue(hideableValueGetValue(fields.timeZoneId))
        : null;
    const isDateTBD = fields.isDateTBD
        ? overridableValueGetValue(fields.isDateTBD)
        : false;

    const dateData: DoppeActionTypeEvent.EventDateData = {
        startDate: startDate,
        startTime: startTime,
        endDate: endDate,
        endTime: endTime,
        timeZoneId: timeZoneId,
        isDateTBD: isDateTBD,
        customText: hideableValueCreateHidden(''),
    };

    const hasOriginalField = (fields.eventDate && overridableValueIsOriginal(hideableValueGetValue(fields.eventDate))) ||
        (fields.isDateTBD && overridableValueIsOriginal(fields.isDateTBD));

    return hasOriginalField
        ? overridableValueCreateOriginal(dateData)
        : dateData;
}



export function doppeActionTypeEventEnsureDateDataCustomText(
    eventDateData: HideableValue<OverridableValue<DoppeActionTypeEvent.EventDateData>>
): HideableValue<OverridableValue<DoppeActionTypeEvent.EventDateData>> {
    return hideableValueModifyValue(eventDateData, overridableValue => {
        const innerValue = overridableValueGetValue(overridableValue);
        if (!innerValue || innerValue.customText !== undefined) {
            return overridableValue;
        }
        const patched = {...innerValue, customText: hideableValueCreateHidden('')};
        return overridableValueIsOriginal(overridableValue)
            ? overridableValueCreateOriginal(patched)
            : patched;
    });
}

export function calculatePriceDataForEvent(event: DoppeDtoEvent): DoppeActionPriceData {
    const formattedPrice = getFormattedEventPrice(event);
    return {actualPrice: formattedPrice, discountPrice: ''};
}

export function calculateActualPriceForEvent(event: DoppeDtoEvent): HideableValue<OverridableValue<DoppeActionPriceData>> {
    const priceData = calculatePriceDataForEvent(event);
    return event.lowestPrice || event.highestPrice
        ? overridableValueCreateOriginal(priceData)
        : hideableValueCreateHidden(overridableValueCreateOriginal(priceData));
}

export function getFormattedEventPrice(event: DoppeDtoEvent) {
    if (event.lowestPrice !== event.highestPrice && event.lowestPrice && event.highestPrice) {
        return  `${event.lowestPrice} - ${event.highestPrice}`;

    }
    else if (event.lowestPrice) {
        return event.lowestPrice;
    }
    else if (event.highestPrice) {
        return event.highestPrice;
    }
    else {
        return '---';
    }
}

/**
 * Checks whether any overridable field in the given event settings
 * has been explicitly marked as original (i.e., already migrated).
 */
export function doppeActionTypeEventHasAnyOriginalOverridableField(settings: DoppeActionTypeEvent.EventSettings): boolean {
    return overridableValueIsOriginal(settings.eventImages) ||
        overridableValueIsOriginal(hideableValueGetValue(settings.priceData)) ||
        overridableValueIsOriginal(hideableValueGetValue(settings.eventTitle)) ||
        overridableValueIsOriginal(hideableValueGetValue(settings.description)) ||
        overridableValueIsOriginal(hideableValueGetValue(settings.eventDateData)) ||
        overridableValueIsOriginal(hideableValueGetValue(settings.eventLocation));
}

/**
 * Migrates existing connected event settings to use OverridableValue.
 * For existing connected events (eventProvider !== null) that haven't been migrated yet,
 * marks eventDateData as "original" to preserve the current behavior where date data
 * is always taken from the bound entity.
 */
export function doppeActionTypeEventMigrateOverridableValues<T extends DoppeActionTypeEvent.EventSettings>(settings: T): T {
    if (settings.eventProvider !== null && !doppeActionTypeEventHasAnyOriginalOverridableField(settings)) {
        return {
            ...settings,
            eventDateData: hideableValueModifyValue(
                settings.eventDateData,
                value => overridableValueCreateOriginal(overridableValueGetValue(value))
            ),
            eventLocation: hideableValueModifyValue(
                settings.eventLocation,
                value => overridableValueCreateOriginal(overridableValueGetValue(value))
            ),
        };
    }

    return settings;
}